import { useEffect, useState } from "react";
import { Activity, CheckCircle, AlertTriangle, XCircle } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { useSystemHealth } from "@/hooks/admin/useEnterprise";
import type { ServiceStatus, ServiceStatusValue, OverallStatusValue } from "@/lib/admin-api/enterprise";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const OVERALL_CONFIG: Record<
  OverallStatusValue,
  { readonly label: string; readonly bg: string; readonly text: string; readonly icon: typeof CheckCircle }
> = {
  operational: {
    label: "Tous les systèmes opérationnels",
    bg: "bg-emerald-50 border-emerald-200",
    text: "text-emerald-800",
    icon: CheckCircle,
  },
  degraded: {
    label: "Dégradé — Certains services rencontrent des problèmes",
    bg: "bg-amber-50 border-amber-200",
    text: "text-amber-800",
    icon: AlertTriangle,
  },
  incident: {
    label: "Incident en cours — Des services sont indisponibles",
    bg: "bg-red-50 border-red-200",
    text: "text-red-800",
    icon: XCircle,
  },
};

const SERVICE_STATUS_COLORS: Record<ServiceStatusValue, string> = {
  operational: "bg-emerald-100 text-emerald-800 border-emerald-200",
  degraded: "bg-amber-100 text-amber-800 border-amber-200",
  down: "bg-red-100 text-red-800 border-red-200",
};

const SERVICE_STATUS_LABELS: Record<ServiceStatusValue, string> = {
  operational: "Opérationnel",
  degraded: "Dégradé",
  down: "Hors service",
};

const SERVICE_DOT_COLORS: Record<ServiceStatusValue, string> = {
  operational: "bg-emerald-500",
  degraded: "bg-amber-500",
  down: "bg-red-500",
};

// ---------------------------------------------------------------------------
// Service Card
// ---------------------------------------------------------------------------

interface ServiceCardProps {
  readonly service: ServiceStatus;
}

function ServiceCard({ service }: ServiceCardProps) {
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <span
            className={`mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full ${SERVICE_DOT_COLORS[service.status]}`}
          />
          <h3 className="text-sm font-semibold text-stone-900">{service.name}</h3>
        </div>
        <span
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${SERVICE_STATUS_COLORS[service.status]}`}
        >
          {SERVICE_STATUS_LABELS[service.status]}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs text-stone-500">Temps de réponse</p>
          <p className="text-sm font-semibold text-stone-900">
            {service.response_time_ms != null ? `${service.response_time_ms} ms` : "—"}
          </p>
        </div>
        <div>
          <p className="text-xs text-stone-500">Uptime 30 j</p>
          <p className="text-sm font-semibold text-stone-900">
            {typeof service.uptime_percent === "number"
              ? `${service.uptime_percent.toFixed(2)} %`
              : "—"}
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function SystemHealth() {
  const { data, isLoading, dataUpdatedAt } = useSystemHealth();
  const [lastChecked, setLastChecked] = useState<string | null>(null);

  useEffect(() => {
    if (dataUpdatedAt) {
      setLastChecked(new Date(dataUpdatedAt).toLocaleTimeString("fr-SN"));
    }
  }, [dataUpdatedAt]);

  const overall = data?.overall ?? "operational";
  const services = data?.services ?? [];
  const uptime30d = data?.uptime_30d_percent;

  const overallConfig = OVERALL_CONFIG[overall];
  const OverallIcon = overallConfig.icon;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <Activity className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                État du système
              </h1>
              <p className="text-sm text-stone-500">
                Surveillance en temps réel des services
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-stone-400">Actualisation automatique toutes les 30 s</p>
            {lastChecked && (
              <p className="text-xs text-stone-500">
                Dernière vérification : {lastChecked}
              </p>
            )}
          </div>
        </div>

        {/* Overall status banner */}
        <div className={`flex items-center gap-3 rounded-lg border px-4 py-4 ${overallConfig.bg}`}>
          <OverallIcon className={`h-5 w-5 shrink-0 ${overallConfig.text}`} />
          <div>
            <p className={`text-sm font-semibold ${overallConfig.text}`}>
              {overallConfig.label}
            </p>
            {uptime30d != null && (
              <p className={`text-xs mt-0.5 ${overallConfig.text} opacity-75`}>
                Disponibilité globale (30 j) : {uptime30d.toFixed(2)} %
              </p>
            )}
          </div>
        </div>

        {/* Service cards */}
        {isLoading ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="h-28 animate-pulse rounded-lg border border-stone-100 bg-stone-50"
              />
            ))}
          </div>
        ) : services.length === 0 ? (
          <div className="rounded-lg border border-stone-200 bg-white py-12 text-center">
            <p className="text-sm text-stone-500">Aucun service supervisé.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {services.map((service) => (
              <ServiceCard key={service.name} service={service} />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
